import React from 'react';
import { Button, Empty, Input, List, Space, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

import { useEditorDoc, useEditorDispatch, useEditorUi, useEditorMeta } from '../../EditorProvider';
import type { WordsearchDoc } from './doc';
import type { WordsearchAction } from './reducer';
import type { WordsearchUi } from './ui';

// Painel lateral: título do passatempo, botão de preencher as células vazias e a
// lista das palavras colocadas (solução + dica + remover). A dica é obrigatória
// (o `validate` cobra como `error`), então o campo vazio fica em destaque visual —
// é o que bloqueia o Salvar no shell (`EditorShell` já desabilita o botão).
//
// Passar o mouse numa linha seleciona a palavra (`ui.selectedRef`), o que faz o
// Canvas destacar as células dela na grade.

export const Inspector: React.FC = () => {
  const doc = useEditorDoc<WordsearchDoc>();
  const { do: dispatchAction } = useEditorDispatch();
  const { editavel } = useEditorMeta();
  const [, setUi] = useEditorUi<WordsearchUi>();
  const dispatch = (action: WordsearchAction) => dispatchAction(action);

  const emptyCount = doc.letters.filter((letter) => letter === '').length;

  return (
    <div style={{ minWidth: 300, maxWidth: 380 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Input
          addonBefore="Título"
          value={doc.titulo}
          disabled={!editavel}
          onChange={(e) => dispatch({ type: 'SET_TITULO', titulo: e.target.value })}
        />

        <Button
          block
          disabled={!editavel || emptyCount === 0}
          onClick={() => dispatch({ type: 'FILL_RANDOM' })}
        >
          {emptyCount === 0 ? 'Grade completa' : `Preencher ${emptyCount} célula(s) vazia(s)`}
        </Button>

        {/* Sem isto o teclado da grade fica invisível: nada na tela conta que dá para
            clicar numa célula e digitar. */}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Arraste na grade para colocar uma palavra. Para corrigir o recheio, clique
          numa célula e digite (A–Z; Backspace apaga, as setas movem).
        </Typography.Text>

        <div>
          <Typography.Text strong>Palavras ({doc.words.length})</Typography.Text>
          {doc.words.length === 0 ? (
            <Empty description="Arraste na grade para colocar uma palavra" />
          ) : (
            <List
              size="small"
              dataSource={doc.words}
              rowKey={(w) => w.ref}
              renderItem={(w) => (
                <List.Item
                  onMouseEnter={() => setUi((prev) => ({ ...prev, selectedRef: w.ref }))}
                  onMouseLeave={() => setUi((prev) => ({ ...prev, selectedRef: null }))}
                  actions={[
                    <Button
                      key="del"
                      type="text"
                      danger
                      disabled={!editavel}
                      icon={<DeleteOutlined />}
                      // O `ref` entra no rótulo porque duas palavras podem ter a MESMA
                      // solução: só com `w.solution` os rótulos ficariam idênticos,
                      // que é exatamente o que o aria-label existia para resolver.
                      aria-label={`Remover ${w.solution} (${w.ref})`}
                      onClick={() => {
                        dispatch({ type: 'REMOVE_WORD', ref: w.ref });
                        // O unmount da linha não dispara `mouseleave`: sem isto o
                        // `selectedRef` fica apontando para uma palavra que não existe
                        // mais.
                        setUi((prev) =>
                          prev.selectedRef === w.ref ? { ...prev, selectedRef: null } : prev,
                        );
                      }}
                    />,
                  ]}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size={2}>
                    <Typography.Text code>{w.solution}</Typography.Text>
                    <Input
                      size="small"
                      placeholder="Dica (obrigatória)"
                      aria-label={`Dica de ${w.solution} (${w.ref})`}
                      status={w.clue.trim() === '' ? 'error' : undefined}
                      disabled={!editavel}
                      value={w.clue}
                      onChange={(e) =>
                        dispatch({ type: 'SET_CLUE', ref: w.ref, clue: e.target.value })
                      }
                    />
                  </Space>
                </List.Item>
              )}
            />
          )}
        </div>
      </Space>
    </div>
  );
};
